import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlightsStore, Flight, FlightStatus } from '../store/flights';

interface FlightsListProps {
  projectId: string;
  openCreateModal?: boolean;
  defaultStartDate?: Date;
  onCreateModalClose?: () => void;
}

const STATUS_CONFIG: Record<FlightStatus, { labelKey: string; color: string; icon: string }> = {
  draft: { labelKey: 'status.draft', color: 'bg-gray-100 text-gray-700', icon: '📝' },
  active: { labelKey: 'status.active', color: 'bg-green-100 text-green-700', icon: '🚀' },
  paused: { labelKey: 'status.paused', color: 'bg-yellow-100 text-yellow-700', icon: '⏸️' },
  completed: { labelKey: 'status.completed', color: 'bg-blue-100 text-blue-700', icon: '✓' },
  archived: { labelKey: 'status.archived', color: 'bg-gray-100 text-gray-500', icon: '📦' },
};

function formatDate(dateStr: string): string {
  // Parse as local date to avoid timezone issues (YYYY-MM-DD format)
  const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateRange(start: string, end: string): string {
  return `${formatDate(start)} – ${formatDate(end)}`;
}

function getDaysRemaining(endDate: string): { days: number; textKey: string; color: string } {
  // Parse as local date to avoid timezone issues
  const [year, month, day] = endDate.split('T')[0].split('-').map(Number);
  const end = new Date(year, month - 1, day);
  const now = new Date();
  // Reset time to start of day for accurate day comparison
  now.setHours(0, 0, 0, 0);
  const diffMs = end.getTime() - now.getTime();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (days < 0) {
    return { days, textKey: 'ended', color: 'text-gray-500' };
  }
  if (days === 0) {
    return { days, textKey: 'today', color: 'text-red-600' };
  }
  if (days === 1) {
    return { days, textKey: 'day', color: 'text-orange-600' };
  }
  if (days <= 7) {
    return { days, textKey: 'days', color: 'text-orange-500' };
  }
  return { days, textKey: 'days', color: 'text-gray-600' };
}

export function FlightsList({ projectId, openCreateModal, defaultStartDate, onCreateModalClose }: FlightsListProps) {
  const { t } = useTranslation('flights');
  const { flights, fetchFlights, activateFlight, pauseFlight, completeFlight, reactivateFlight, duplicateFlight, deleteFlight, reorderFlights, isLoading, error } = useFlightsStore();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Open create modal from external trigger
  useEffect(() => {
    if (openCreateModal) {
      setShowCreateModal(true);
    }
  }, [openCreateModal]);
  const [editingFlight, setEditingFlight] = useState<Flight | null>(null);
  const [expandedFlight, setExpandedFlight] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Drag & Drop state
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const draggedRef = useRef<string | null>(null);

  useEffect(() => {
    fetchFlights(projectId);
  }, [projectId, fetchFlights]);

  const handleStatusAction = async (flight: Flight, action: 'activate' | 'pause' | 'complete' | 'reactivate') => {
    setActionLoading(flight.id);
    try {
      if (action === 'activate') {
        await activateFlight(flight.id);
      } else if (action === 'pause') {
        await pauseFlight(flight.id);
      } else if (action === 'complete') {
        await completeFlight(flight.id);
      } else if (action === 'reactivate') {
        await reactivateFlight(flight.id);
      }
    } catch {
      // Error handled in store
    } finally {
      setActionLoading(null);
    }
  };

  const handleDuplicate = async (flight: Flight) => {
    setActionLoading(flight.id);
    try {
      await duplicateFlight(flight.id);
    } catch {
      // Error handled in store
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (flight: Flight) => {
    if (!confirm(t('confirmDelete'))) return;
    setActionLoading(flight.id);
    try {
      await deleteFlight(flight.id);
    } catch {
      // Error handled in store
    } finally {
      setActionLoading(null);
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e: React.DragEvent, flight: Flight) => {
    draggedRef.current = flight.id;
    setDraggedId(flight.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', flight.id);
    // Add a slight delay to show the dragging state
    setTimeout(() => {
      const element = e.target as HTMLElement;
      element.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    const element = e.target as HTMLElement;
    element.style.opacity = '1';
    setDraggedId(null);
    setDragOverId(null);
    draggedRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent, flightId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (flightId !== draggedRef.current) {
      setDragOverId(flightId);
    }
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = async (e: React.DragEvent, targetFlightId: string) => {
    e.preventDefault();
    const draggedFlightId = draggedRef.current;

    if (!draggedFlightId || draggedFlightId === targetFlightId) {
      setDragOverId(null);
      return;
    }

    // Calculate new order
    const currentOrder = flights.map(f => f.id);
    const draggedIndex = currentOrder.indexOf(draggedFlightId);
    const targetIndex = currentOrder.indexOf(targetFlightId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Remove dragged item and insert at target position
    const newOrder = [...currentOrder];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedFlightId);

    setDragOverId(null);
    setDraggedId(null);

    // Update server
    try {
      await reorderFlights(projectId, newOrder);
    } catch {
      // Revert handled by store
      fetchFlights(projectId);
    }
  };

  if (isLoading && flights.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t('loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-lg">🛫</span>
          <h3 className="font-semibold text-gray-900">{t('title')}</h3>
          <span className="text-sm text-gray-500">({flights.length})</span>
          {flights.length > 1 && (
            <span className="text-xs text-gray-400 ml-2">{t('dragToSort')}</span>
          )}
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('newFlight')}
        </button>
      </div>

      {/* Flights List */}
      {flights.length === 0 ? (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
          <div className="text-3xl mb-2">🛫</div>
          <p>{t('empty.title')}</p>
          <p className="text-sm mt-1">{t('empty.description')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {flights.map((flight) => {
            const statusConfig = STATUS_CONFIG[flight.status];
            const daysInfo = getDaysRemaining(flight.end_date);
            const isExpanded = expandedFlight === flight.id;
            const isActionLoading = actionLoading === flight.id;
            const isDragging = draggedId === flight.id;
            const isDragOver = dragOverId === flight.id;

            return (
              <div
                key={flight.id}
                draggable
                onDragStart={(e) => handleDragStart(e, flight)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, flight.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, flight.id)}
                className={`border rounded-lg overflow-hidden transition-all ${
                  flight.status === 'active' ? 'border-green-300 bg-green-50/30' : 'border-gray-200 bg-white'
                } ${isDragging ? 'opacity-50 scale-[0.98]' : ''} ${
                  isDragOver ? 'border-blue-400 border-2 shadow-lg' : ''
                }`}
              >
                {/* Main Row */}
                <div
                  className="p-4 flex items-center gap-3 cursor-grab active:cursor-grabbing hover:bg-gray-50/50"
                >
                  {/* Drag Handle */}
                  <div className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                    </svg>
                  </div>

                  {/* Status Icon */}
                  <div className="text-2xl">{statusConfig.icon}</div>

                  {/* Info */}
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => setEditingFlight(flight)}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{flight.name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusConfig.color}`}>
                        {t(statusConfig.labelKey)}
                      </span>
                      <span className="text-xs text-gray-400">{flight.display_id}</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-4">
                      <span>{formatDateRange(flight.start_date, flight.end_date)}</span>
                      {flight.status === 'active' && (
                        <span className={daysInfo.color}>
                          {daysInfo.textKey === 'day' ? `1 ${t('day')}` : daysInfo.textKey === 'days' ? `${daysInfo.days} ${t('days')}` : t(daysInfo.textKey)} {t('remaining')}
                        </span>
                      )}
                      {flight.stats.posts_created !== undefined && flight.stats.posts_created > 0 && (
                        <span className="text-gray-600">
                          {t('postsCreated')}: {flight.stats.posts_created}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {flight.status === 'draft' && (
                      <button
                        onClick={() => handleStatusAction(flight, 'activate')}
                        disabled={isActionLoading}
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50"
                      >
                        {isActionLoading ? '...' : `▶ ${t('activate')}`}
                      </button>
                    )}
                    {flight.status === 'active' && (
                      <>
                        <button
                          onClick={() => handleStatusAction(flight, 'pause')}
                          disabled={isActionLoading}
                          className="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 disabled:opacity-50"
                        >
                          {isActionLoading ? '...' : `⏸ ${t('pause')}`}
                        </button>
                        <button
                          onClick={() => handleStatusAction(flight, 'complete')}
                          disabled={isActionLoading}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                        >
                          {isActionLoading ? '...' : `✓ ${t('complete')}`}
                        </button>
                      </>
                    )}
                    {flight.status === 'paused' && (
                      <button
                        onClick={() => handleStatusAction(flight, 'activate')}
                        disabled={isActionLoading}
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50"
                      >
                        {isActionLoading ? '...' : `▶ ${t('resumeFlight')}`}
                      </button>
                    )}
                    {flight.status === 'completed' && (
                      <button
                        onClick={() => handleStatusAction(flight, 'reactivate')}
                        disabled={isActionLoading}
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50"
                      >
                        {isActionLoading ? '...' : `🔄 ${t('reactivate')}`}
                      </button>
                    )}
                    <button
                      onClick={() => handleDuplicate(flight)}
                      disabled={isActionLoading}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                      title={t('duplicate')}
                    >
                      {isActionLoading ? '...' : '📋'}
                    </button>
                    <button
                      onClick={() => setExpandedFlight(isExpanded ? null : flight.id)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                    >
                      <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4 space-y-4" onClick={(e) => e.stopPropagation()}>
                    {/* Description */}
                    {flight.description && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1">{t('description')}</div>
                        <p className="text-sm text-gray-700">{flight.description}</p>
                      </div>
                    )}

                    {/* AI Context */}
                    {flight.ai_context && Object.keys(flight.ai_context).length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-2">{t('aiContext')}</div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {flight.ai_context.brand_voice && (
                            <div>
                              <span className="text-gray-500">{t('brandVoice')}:</span>{' '}
                              <span className="text-gray-700">{flight.ai_context.brand_voice}</span>
                            </div>
                          )}
                          {flight.ai_context.target_audience && (
                            <div>
                              <span className="text-gray-500">{t('targetAudience')}:</span>{' '}
                              <span className="text-gray-700">{flight.ai_context.target_audience}</span>
                            </div>
                          )}
                          {flight.ai_context.key_messages && flight.ai_context.key_messages.length > 0 && (
                            <div className="col-span-2">
                              <span className="text-gray-500">{t('keyMessages')}:</span>{' '}
                              <span className="text-gray-700">{flight.ai_context.key_messages.join(', ')}</span>
                            </div>
                          )}
                          {flight.ai_context.hashtag_strategy && flight.ai_context.hashtag_strategy.length > 0 && (
                            <div className="col-span-2">
                              <span className="text-gray-500">{t('hashtagStrategy')}:</span>{' '}
                              <span className="text-blue-600">{flight.ai_context.hashtag_strategy.join(' ')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Goals */}
                    {flight.goals && Object.keys(flight.goals).length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-2">{t('goals')}</div>
                        <div className="flex flex-wrap gap-3 text-sm">
                          {flight.goals.post_count && (
                            <span className="px-2 py-1 bg-white rounded border text-gray-700">
                              {flight.goals.post_count} {t('scheduledCount', { count: flight.goals.post_count })}
                            </span>
                          )}
                          {flight.goals.platforms && flight.goals.platforms.length > 0 && (
                            <span className="px-2 py-1 bg-white rounded border text-gray-700">
                              {flight.goals.platforms.map(p => p === 'facebook' ? 'FB' : 'IG').join(' + ')}
                            </span>
                          )}
                          {flight.goals.posting_frequency && (
                            <span className="px-2 py-1 bg-white rounded border text-gray-700">
                              {flight.goals.posting_frequency}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Stats */}
                    {flight.stats && Object.values(flight.stats).some(v => v !== undefined && v > 0) && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-2">{t('common:statistics', 'Statistiken')}</div>
                        <div className="flex gap-4 text-sm">
                          {flight.stats.posts_created !== undefined && (
                            <div className="text-center">
                              <div className="text-xl font-bold text-gray-900">{flight.stats.posts_created}</div>
                              <div className="text-xs text-gray-500">{t('common:created', 'Erstellt')}</div>
                            </div>
                          )}
                          {flight.stats.posts_published !== undefined && (
                            <div className="text-center">
                              <div className="text-xl font-bold text-green-600">{flight.stats.posts_published}</div>
                              <div className="text-xs text-gray-500">{t('published')}</div>
                            </div>
                          )}
                          {flight.stats.images_generated !== undefined && (
                            <div className="text-center">
                              <div className="text-xl font-bold text-purple-600">{flight.stats.images_generated}</div>
                              <div className="text-xs text-gray-500">{t('common:images', 'Bilder')}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t">
                      <button
                        onClick={() => setEditingFlight(flight)}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        {t('edit')}
                      </button>
                      <button
                        onClick={() => handleDuplicate(flight)}
                        disabled={isActionLoading}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                      >
                        {t('duplicate')}
                      </button>
                      {(flight.status === 'draft' || flight.status === 'completed') && (
                        <button
                          onClick={() => handleDelete(flight)}
                          disabled={isActionLoading}
                          className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                        >
                          {t('delete')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateFlightModal
          projectId={projectId}
          defaultStartDate={defaultStartDate}
          onClose={() => {
            setShowCreateModal(false);
            onCreateModalClose?.();
          }}
        />
      )}

      {/* Edit Modal */}
      {editingFlight && (
        <EditFlightModal
          flight={editingFlight}
          onClose={() => setEditingFlight(null)}
        />
      )}
    </div>
  );
}

// Helper to format date to YYYY-MM-DD in local timezone (not UTC)
function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Create Flight Modal
function CreateFlightModal({ projectId, defaultStartDate, onClose }: { projectId: string; defaultStartDate?: Date; onClose: () => void }) {
  const { t } = useTranslation('flights');
  const { createFlight } = useFlightsStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const startDateStr = defaultStartDate
    ? formatLocalDate(defaultStartDate)
    : formatLocalDate(new Date());
  const endDate = defaultStartDate
    ? new Date(defaultStartDate.getTime() + 30 * 24 * 60 * 60 * 1000)
    : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: startDateStr,
    end_date: formatLocalDate(endDate),
    brand_voice: '',
    target_audience: '',
    key_messages: '',
    hashtags: '',
    post_count: '',
    platforms: ['facebook', 'instagram'] as string[],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await createFlight({
        project_id: projectId,
        name: formData.name,
        description: formData.description || undefined,
        start_date: formData.start_date,
        end_date: formData.end_date,
        ai_context: {
          brand_voice: formData.brand_voice || undefined,
          target_audience: formData.target_audience || undefined,
          key_messages: formData.key_messages ? formData.key_messages.split(',').map(s => s.trim()) : undefined,
          hashtag_strategy: formData.hashtags ? formData.hashtags.split(/[\s,]+/).filter(Boolean) : undefined,
        },
        goals: {
          post_count: formData.post_count ? parseInt(formData.post_count) : undefined,
          platforms: formData.platforms.length > 0 ? formData.platforms : undefined,
        },
      });
      onClose();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlatformToggle = (platform: string) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <span>🛫</span> {t('newFlight')}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Basic Info */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('name')} *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('placeholder.name')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('description')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={2}
                placeholder={t('placeholder.description')}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('startDate')} *</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('endDate')} *</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* AI Context */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>🤖</span> {t('aiContext')} ({t('aiContextDescription')})
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('brandVoice')}</label>
                <input
                  type="text"
                  value={formData.brand_voice}
                  onChange={(e) => setFormData(prev => ({ ...prev, brand_voice: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.brandVoice')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('targetAudience')}</label>
                <input
                  type="text"
                  value={formData.target_audience}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_audience: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.targetAudience')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('keyMessages')} ({t('keyMessagesHint')})</label>
                <input
                  type="text"
                  value={formData.key_messages}
                  onChange={(e) => setFormData(prev => ({ ...prev, key_messages: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.keyMessages')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('hashtagStrategy')}</label>
                <input
                  type="text"
                  value={formData.hashtags}
                  onChange={(e) => setFormData(prev => ({ ...prev, hashtags: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.hashtags')}
                />
              </div>
            </div>
          </div>

          {/* Goals */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>🎯</span> {t('goals')}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('plannedPostCount')}</label>
                <input
                  type="number"
                  value={formData.post_count}
                  onChange={(e) => setFormData(prev => ({ ...prev, post_count: e.target.value }))}
                  className="w-32 px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.postCount')}
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('platforms')}</label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.platforms.includes('facebook')}
                      onChange={() => handlePlatformToggle('facebook')}
                      className="mr-2 h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="flex items-center gap-1 text-sm">
                      <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                      Facebook
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.platforms.includes('instagram')}
                      onChange={() => handlePlatformToggle('instagram')}
                      className="mr-2 h-4 w-4 text-pink-600 rounded"
                    />
                    <span className="flex items-center gap-1 text-sm">
                      <svg className="w-4 h-4 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                      Instagram
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
            >
              {t('common:cancel', 'Abbrechen')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? t('common:creating', 'Erstelle...') : t('createFlight')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Flight Modal
function EditFlightModal({
  flight,
  onClose,
}: {
  flight: Flight;
  onClose: () => void;
}) {
  const { t } = useTranslation('flights');
  const { updateFlight } = useFlightsStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: flight.name,
    description: flight.description || '',
    start_date: flight.start_date.split('T')[0],
    end_date: flight.end_date.split('T')[0],
    brand_voice: flight.ai_context?.brand_voice || '',
    target_audience: flight.ai_context?.target_audience || '',
    key_messages: flight.ai_context?.key_messages?.join(', ') || '',
    hashtags: flight.ai_context?.hashtag_strategy?.join(' ') || '',
    post_count: flight.goals?.post_count?.toString() || '',
    platforms: flight.goals?.platforms || ['facebook', 'instagram'],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await updateFlight(flight.id, {
        name: formData.name,
        description: formData.description || undefined,
        start_date: formData.start_date,
        end_date: formData.end_date,
        ai_context: {
          brand_voice: formData.brand_voice || undefined,
          target_audience: formData.target_audience || undefined,
          key_messages: formData.key_messages ? formData.key_messages.split(',').map(s => s.trim()) : undefined,
          hashtag_strategy: formData.hashtags ? formData.hashtags.split(/[\s,]+/).filter(Boolean) : undefined,
        },
        goals: {
          post_count: formData.post_count ? parseInt(formData.post_count) : undefined,
          platforms: formData.platforms.length > 0 ? formData.platforms : undefined,
        },
      });
      onClose();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlatformToggle = (platform: string) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span>✏️</span> {t('editFlight')}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">{flight.display_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Basic Info */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('name')} *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('placeholder.name')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('description')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={2}
                placeholder={t('placeholder.description')}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('startDate')} *</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('endDate')} *</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* AI Context */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>🤖</span> {t('aiContext')} ({t('aiContextDescription')})
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('brandVoice')}</label>
                <input
                  type="text"
                  value={formData.brand_voice}
                  onChange={(e) => setFormData(prev => ({ ...prev, brand_voice: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.brandVoice')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('targetAudience')}</label>
                <input
                  type="text"
                  value={formData.target_audience}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_audience: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.targetAudience')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('keyMessages')} ({t('keyMessagesHint')})</label>
                <input
                  type="text"
                  value={formData.key_messages}
                  onChange={(e) => setFormData(prev => ({ ...prev, key_messages: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.keyMessages')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('hashtagStrategy')}</label>
                <input
                  type="text"
                  value={formData.hashtags}
                  onChange={(e) => setFormData(prev => ({ ...prev, hashtags: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.hashtags')}
                />
              </div>
            </div>
          </div>

          {/* Goals */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>🎯</span> {t('goals')}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('plannedPostCount')}</label>
                <input
                  type="number"
                  value={formData.post_count}
                  onChange={(e) => setFormData(prev => ({ ...prev, post_count: e.target.value }))}
                  className="w-32 px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.postCount')}
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('platforms')}</label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.platforms.includes('facebook')}
                      onChange={() => handlePlatformToggle('facebook')}
                      className="mr-2 h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="flex items-center gap-1 text-sm">
                      <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                      Facebook
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.platforms.includes('instagram')}
                      onChange={() => handlePlatformToggle('instagram')}
                      className="mr-2 h-4 w-4 text-pink-600 rounded"
                    />
                    <span className="flex items-center gap-1 text-sm">
                      <svg className="w-4 h-4 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                      Instagram
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
            >
              {t('common:cancel', 'Abbrechen')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? t('saving') : t('saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
